import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useI18n } from "../hooks/useI18n";
import { useKairosApi } from "../hooks/useKairosApi";
import { useDaphne } from "../hooks/useDaphne";
import { useCountryConfig } from "../hooks/useCountryConfig";
import { PageLayout } from "../layout/PageLayout";
import { theme } from "../layout/Theme";
import { getTranslatedFieldByLocale } from "../../utils/translatedFields";
import { getDiseasesNames } from "../hooks/useVaccinesWithDiseases";
import { formatDateForApi } from "../../utils/date";
import { type Evc } from "@syadem/evc-js";
import { CreateVaccinationActBody, HealthRecordGenderEnum } from "@syadem/kairos-citizen-js";
import { dayjs } from "../../utils/dayjs";
import { LightStyledInput } from "../components/mui/LightStyledInput";
import { Dayjs } from "dayjs";
import { LightStyledSelect } from "../components/mui/LightStyledSelect";
import { parseEvcToken } from "../components/evc/parseEvcToken";

interface VaccinationToImport {
  id: number;
  nuvaCode: number;
  vaccineName: string;
  diseasesLabel: string;
  countryCode: string;
  performedOn: string;
  booster?: boolean;
  selected: boolean;
}

type PageStep = "loading" | "form" | "importing" | "done" | "error";

export function EvcImportPage() {
  const { t, locale } = useI18n();
  const api = useKairosApi();
  const daphne = useDaphne();
  const location = useLocation();
  const navigate = useNavigate();
  const { zipCode: zipCodeConfig } = useCountryConfig();
  const evcToken = useMemo(() => new URLSearchParams(location.search).get("evc"), [location.search]);

  const [step, setStep] = useState<PageStep>("loading");
  const [parsedEvc, setParsedEvc] = useState<Evc | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [vaccinations, setVaccinations] = useState<VaccinationToImport[]>([]);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0, errors: 0 });
  const [healthRecordId, setHealthRecordId] = useState<string | null>(null);

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState<Dayjs | null>(null);
  const [gender, setGender] = useState<HealthRecordGenderEnum | "">("");
  const [zipCode, setZipCode] = useState("");
  const [invalidGender, setInvalidGender] = useState(false);
  const [invalidZipCode, setInvalidZipCode] = useState(false);
  const genderRef = useRef<HTMLDivElement>(null);
  const zipCodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!evcToken && step === "loading") {
      navigate("/health-records", { replace: true });
    }
  }, [evcToken, navigate, step]);

  // Parse EVC token
  useEffect(() => {
    if (!evcToken) return;
    let cancelled = false;
    (async () => {
      try {
        const evc = await parseEvcToken(evcToken);
        if (cancelled) return;
        setParsedEvc(evc);

        // Pre-fill form fields from EVC
        setFirstName(evc.name.firstName);
        setLastName(evc.name.lastName);
        setBirthDate(dayjs.utc(evc.dateOfBirth));
        if (evc.metadata?.gender) {
          setGender(evc.metadata.gender);
        }

        // Map vaccinations
        const mapped: VaccinationToImport[] = (evc.vaccinations || []).map((v) => {
          const vaccine = daphne.repositories.vaccines.all().find((c) => c.code === v.nuvaCode);
          const vaccineName = vaccine ? getTranslatedFieldByLocale(locale, vaccine.name) : `NUVA ${v.nuvaCode}`;
          const diseases = vaccine ? daphne.queries.diseasesByVaccine(vaccine) : [];
          const diseasesLabel = getDiseasesNames(diseases, locale || "en");

          return {
            id: v.id,
            nuvaCode: v.nuvaCode,
            vaccineName: vaccineName || `NUVA ${v.nuvaCode}`,
            diseasesLabel: diseasesLabel || "-",
            countryCode: v.countryCode,
            performedOn: v.performedOn.toISOString().slice(0, 10),
            booster: v.booster,
            selected: true,
          };
        });

        setVaccinations(mapped);
        setStep("form");
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof Error ? err.message : t("evc_onboarding.parse_error"));
        setStep("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [evcToken, daphne, locale, t]);

  const needsZipCode = zipCodeConfig.requiredForHealthRecord;

  const selectedCount = useMemo(() => vaccinations.filter((v) => v.selected).length, [vaccinations]);

  const toggleVaccination = useCallback((id: number) => {
    setVaccinations((prev) => prev.map((v) => (v.id === id ? { ...v, selected: !v.selected } : v)));
  }, []);

  const validateZipCode = useCallback(
    (value: string): boolean => {
      if (!needsZipCode && value.trim() === "") return true;
      return !!value.match(zipCodeConfig.regex);
    },
    [needsZipCode, zipCodeConfig.regex],
  );

  const handleSkip = useCallback(() => {
    navigate("/health-records", { replace: true });
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    if (!api) return;

    if (!gender) {
      setInvalidGender(true);
      genderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (needsZipCode && !validateZipCode(zipCode)) {
      setInvalidZipCode(true);
      zipCodeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setStep("importing");
    const selected = vaccinations.filter((v) => v.selected);
    setImportProgress({ done: 0, total: selected.length, errors: 0 });

    try {
      const response = await api.healthRecordApi.createHealthRecord({
        healthRecord: {
          firstNames: firstName.trim(),
          lastName: lastName.trim(),
          customaryName: null,
          birthDate: formatDateForApi((birthDate as Dayjs).toDate()),
          gender: gender as HealthRecordGenderEnum,
          externalId: null,
          zipCode: zipCode.trim() || null,
          birthCountryCode: null,
          birthCityCode: null,
        },
      });

      if (!response?.healthRecord?.id) {
        setErrorMessage(t("evc_onboarding.create_hr_error"));
        setStep("error");
        return;
      }

      const hrId = response.healthRecord.id;
      setHealthRecordId(hrId);

      let done = 0;
      let errors = 0;

      for (const vacc of selected) {
        const vaccine = daphne.repositories.vaccines.all().find((c) => c.code === vacc.nuvaCode);
        if (!vaccine) {
          errors++;
          done++;
          setImportProgress({ done, total: selected.length, errors });
          continue;
        }

        const request: CreateVaccinationActBody = {
          vaccinationAct: {
            performedOn: dayjs.utc(vacc.performedOn).toDate(),
            vaccineId: vaccine.id,
            countryCode: vacc.countryCode || undefined,
            booster: vacc.booster || undefined,
          },
        };

        try {
          await api.vaccinationActApi.createVaccinationAct(hrId, request);
        } catch {
          errors++;
        }
        done++;
        setImportProgress({ done, total: selected.length, errors });
      }

      setStep("done");
    } catch {
      setErrorMessage(t("evc_onboarding.create_hr_error"));
      setStep("error");
    }
  }, [api, firstName, lastName, birthDate, gender, zipCode, needsZipCode, validateZipCode, vaccinations, daphne, t]);

  const handleViewRecord = useCallback(() => {
    if (healthRecordId) {
      navigate(`/health-records/${healthRecordId}`, { replace: true });
    } else {
      navigate("/health-records", { replace: true });
    }
  }, [healthRecordId, navigate]);

  return (
    <PageLayout title={t("evc_onboarding.title")}>
      {step === "loading" && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}>
          <CircularProgress />
          <Typography>{t("evc_onboarding.parsing")}</Typography>
        </Box>
      )}

      {step === "error" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Alert severity="error">{errorMessage || t("evc_onboarding.parse_error")}</Alert>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={handleSkip}>
              {t("evc_onboarding.continue_without_import")}
            </Button>
          </Box>
        </Box>
      )}

      {step === "form" && parsedEvc && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Alert severity="info">{t("evc_onboarding.welcome_message")}</Alert>

          <Paper sx={{ p: 3, border: `solid 1px ${theme.palette.neutral[200]}` }} elevation={0}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightStyledInput
                  fullWidth
                  required
                  label={t("common.user_infos.first_names")}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightStyledInput
                  fullWidth
                  required
                  label={t("common.user_infos.last_name")}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <LightStyledInput
                  fullWidth
                  required
                  disabled
                  label={t("common.user_infos.birth_date")}
                  value={birthDate?.format("YYYY-MM-DD") ?? ""}
                />
              </Grid>
              <Grid ref={genderRef} size={{ xs: 12, md: 6 }}>
                <LightStyledSelect
                  label={t("common.gender.s")}
                  placeholder={`ex : ${t("common.gender.m")}`}
                  renderValue={(g) => t(`common.gender.${g.toLowerCase()}`)}
                  fullWidth
                  required
                  name="gender"
                  value={gender}
                  error={invalidGender}
                  errorMessage={invalidGender ? t("evc_onboarding.gender_required") : undefined}
                  onChange={(e) => {
                    setGender(e.target.value as HealthRecordGenderEnum);
                    setInvalidGender(false);
                  }}
                >
                  <MenuItem value="M">{t("common.gender.m")}</MenuItem>
                  <MenuItem value="F">{t("common.gender.f")}</MenuItem>
                </LightStyledSelect>
              </Grid>

              {/* Zip code: only shown if required for this country */}
              {needsZipCode && (
                <Grid ref={zipCodeRef} size={{ xs: 12, md: 6 }}>
                  <LightStyledInput
                    name="zipCode"
                    fullWidth
                    label={t("common.user_infos.zip_code")}
                    required
                    placeholder={zipCodeConfig.placeholder}
                    value={zipCode}
                    onChange={(e) => {
                      setZipCode(e.target.value);
                      setInvalidZipCode(false);
                    }}
                    onBlur={(e) => setInvalidZipCode(!validateZipCode(e.target.value))}
                    error={invalidZipCode}
                    errorMessage={invalidZipCode ? t("common.errors.zipCode") : undefined}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          {vaccinations.length > 0 && (
            <Accordion
              variant="outlined"
              disableGutters
              sx={{
                border: `1px solid ${theme.palette.neutral[200]}`,
                borderRadius: 2,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: "row-reverse", gap: 1 }}>
                <Typography>
                  {t("evc_onboarding.vaccinations_summary", { selected: selectedCount, total: vaccinations.length })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List disablePadding>
                  {vaccinations.map((vacc, index) => (
                    <ListItem
                      key={vacc.id}
                      divider={index < vaccinations.length - 1}
                      onClick={() => toggleVaccination(vacc.id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <Checkbox checked={vacc.selected} sx={{ mr: 1 }} />
                      <ListItemText
                        primary={vacc.vaccineName}
                        secondary={`${vacc.performedOn} • ${vacc.diseasesLabel}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Button variant="outlined" onClick={handleSkip}>
              {t("evc_onboarding.skip")}
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={selectedCount === 0} disableElevation>
              {t("evc_onboarding.import_selected", { count: selectedCount })}
            </Button>
          </Box>
        </Box>
      )}

      {step === "importing" && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}>
          <CircularProgress />
          <Typography>
            {t("evc_onboarding.importing_progress", {
              done: importProgress.done,
              total: importProgress.total,
            })}
          </Typography>
        </Box>
      )}

      {step === "done" && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, py: 6 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64 }} color="success" />
          <Typography variant="h6">{t("evc_onboarding.success_title")}</Typography>
          <Typography color={theme.palette.neutral[600]}>
            {importProgress.errors > 0
              ? t("evc_onboarding.success_with_errors", {
                  imported: importProgress.done - importProgress.errors,
                  errors: importProgress.errors,
                })
              : t("evc_onboarding.success_message", { count: importProgress.done })}
          </Typography>
          <Button variant="contained" size="large" onClick={handleViewRecord} disableElevation>
            {t("evc_onboarding.view_health_record")}
          </Button>
        </Box>
      )}
    </PageLayout>
  );
}
